import { Component, DestroyRef, OnDestroy, OnInit, inject, input, output } from "@angular/core";
import { NxCell } from "qlik";
import { filter, take, takeUntil } from "rxjs/operators";
import { pipe, Subscription } from "rxjs";
import { NgClass } from "@angular/common";
import { QlikService } from "../../services/qlik.service";
import { IQlikLib } from "../../qliklib/qliklib";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export interface QlikSelectionMap {
  origin: string;
  result: string;
}

@Component({
  selector: "stat-qlik-list",
  templateUrl: "./list.component.html",
  imports: [NgClass],
})
export class ListComponent implements OnInit, OnDestroy {
  private qlikService = inject(QlikService);
  private destroyRef = inject(DestroyRef);

  public stringify = JSON.stringify;

  public readonly id = input<string>();
  public readonly multiselect = input(false);
  public readonly type = input<"checkbox" | "radio" | "buttons">();
  public readonly isLocked = input(false);
  public readonly hideIrrelevant = input(false);
  public readonly default = input<string[]>([]);
  public readonly qlikfield = input<string>(null);
  public readonly sorteringOrientation = input<string>(null);
  public readonly sorteringField = input<string>(null);
  public readonly qlikOptions = input<object>();
  public readonly subId = input<string>(null);
  public readonly tittel = input("");

  public readonly selected = output<{ [key: string]: Array<string | number> }>();

  public list: NxCell[] = [];
  public childrenList: { [key: string]: NxCell[] } = null;

  // SELECTED, LOCKED
  public isSelected = ["S", "L"];
  // OPTION, DESELECTED, ALTERNATIVE
  public isNotSelected = ["O", "D", "A"];
  // EXCLUDED, EXCL_SELECTED, EXCL_LOCKED
  public isDisabled = ["X", "XS", "XL"];

  public alternativeState = "A";

  public optionalState = "O";

  public allChildrenSelected: { [key: string]: boolean } = {};

  public collector: Subscription = new Subscription();

  public ngOnInit() {
    const id = this.id();
    if (this.default().length > 0) {
      this.qlikService.setFilter(id, this.default(), this.type() === "checkbox", this.isLocked());
    }

    let qlikfieldIds: unknown = null;

    const sorteringField = this.sorteringField();
    const subId = this.subId();
    if (sorteringField) {
      qlikfieldIds = [sorteringField, id];
    } else if (subId) {
      const qlikfield = this.qlikfield();
      if (qlikfield) {
        qlikfieldIds = [id, subId, qlikfield];
      } else {
        qlikfieldIds = [id, subId];
      }
    } else {
      qlikfieldIds = id;
    }

    this.qlikService
      .getList(qlikfieldIds, false, subId ? true : false, this.qlikOptions())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cellList: NxCell[] | NxCell[][]) => {
        const selected: { [key: string]: Array<string | number> } = {};
        const sorteringFieldValue = this.sorteringField();
        const subIdValue = this.subId();
        if (sorteringFieldValue) {
          const parents: NxCell[] = [];

          cellList = cellList as NxCell[][];

          if (sorteringFieldValue == "asc") {
            cellList.reverse();
          }

          cellList.forEach(item => {
            if (item.length > 0) {
              const parentKey = item[1].qText;
              if (parents.length == 0) {
                parents.push(item[1]);
              } else if (parents[parents.length - 1].qText != parentKey) {
                parents.push(item[1]);
              }
            }
          });

          cellList = parents;
        } else if (this.qlikfield()) {
          // Handle two dimensional filter
          const parents: NxCell[] = [];
          const children: { [key: string]: NxCell[] } = {};
          (cellList as NxCell[][]).forEach(item => {
            if (item.length > 0) {
              const parentKey = item[0].qText;
              if (parentKey !== "-") {
                if (!children[parentKey]) {
                  parents.push(item[0]);
                  children[parentKey] = [];
                }
                if (item[1].qText !== "-") {
                  item[1].qElemNumber = item[2].qNum; // Override this property to use as second filter in qlik
                  item[1].qState = item[2].qState;
                  // item[1].qFrequency = item[2].qText;
                  children[parentKey].push(item[1]);
                }
              }
            }
          });

          this.childrenList = children;

          selected[this.subId()] = this.getSelectedList(Object.keys(children).reduce((list: NxCell[], key: string) => list.concat(children[key]), []));

          parents.forEach(x => {
            const found = this.childrenList[x.qText].find(y => y.qState == "S");
            if (found) {
              x.qState = "S";
            }

            this.allChildrenSelected[x.qText] = this.childrenList[x.qText].filter(x => x.qState == "S").length == this.childrenList[x.qText].length;
          });

          cellList = parents;
        } else if (subIdValue) {
          // Handle two dimensional filter
          const parents: NxCell[] = [];
          const children: { [key: string]: NxCell[] } = {};
          (cellList as NxCell[][]).forEach(item => {
            if (item.length > 0) {
              const parentKey = item[0].qText;
              if (parentKey !== "-") {
                if (!children[parentKey]) {
                  parents.push(item[0]);
                  children[parentKey] = [];
                }
                if (item[1].qText !== "-") {
                  children[parentKey].push(item[1]);
                }
              }
            }
          });

          this.childrenList = children;

          if (this.multiselect()) {
            selected[subIdValue] = this.getAlternativeList(Object.keys(children).reduce((list: NxCell[], key: string) => list.concat(children[key]), []));
          } else {
            selected[subIdValue] = this.getSelectedList(Object.keys(children).reduce((list: NxCell[], key: string) => list.concat(children[key]), []));
          }

          parents.forEach(x => {
            const found = this.childrenList[x.qText].find(y => this.isSelected.includes(y.qState));
            if (found) {
              x.qState = "S";
            }
          });

          cellList = parents;
        } else {
          // Remove irrelevant items from the list of cells
          if (this.hideIrrelevant()) {
            cellList = (cellList as NxCell[]).filter(item => !this.isDisabled.includes(item.qState));
          }
        }

        // Regular one dimensional filter
        this.list = cellList as NxCell[];
        selected[this.id()] = this.getSelectedList(this.list);
        this.selected.emit(selected);
      })
      .add(this.collector);
  }

  public ngOnDestroy() {
    this.qlikService
      .getQliklib()
      .subscribe((qliklib: IQlikLib) => qliklib?.closeList(this.id()))
      .unsubscribe();

    if (this.qlikService.exportDataListe[this.id()]) {
      delete this.qlikService.exportDataListe[this.id()];
    }

    this.collector.unsubscribe();
  }

  private getAlternativeList(list: NxCell[]) {
    return list.filter(item => this.alternativeState == item.qState).map(item => item.qText);
  }

  private getSelectedList(list: NxCell[]) {
    return list.filter(item => this.isSelected.includes(item.qState)).map(item => item.qText);
  }

  public select(id, item: NxCell, parent: NxCell) {
    const qlikfield = this.qlikfield();
    const subId = this.subId();
    if (qlikfield) {
      let currentSubSelectedValues = [];

      if (id == this.subId()) {
        if (parent != null && this.allChildrenSelected[parent.qText]) {
          currentSubSelectedValues = this.childrenList[parent.qText].map(x => x.qElemNumber);

          currentSubSelectedValues = currentSubSelectedValues.filter(x => x != item.qElemNumber);
        } else {
          // Get child item status
          currentSubSelectedValues = this.childrenList[parent.qText].filter(x => this.isSelected.includes(x.qState)).map(x => x.qElemNumber);

          // Check if the child list has only one element checked and it is the same to select now

          if (currentSubSelectedValues.length == 1 && currentSubSelectedValues[0] == item.qElemNumber) {
            // Select all values field for but exclude the value that is already selected.
            currentSubSelectedValues = this.childrenList[parent.qText].map(x => x.qElemNumber);

            currentSubSelectedValues = currentSubSelectedValues.filter(x => x != item.qElemNumber);
          } else {
            // define value to be selected in Qlik.
            currentSubSelectedValues = [];
            currentSubSelectedValues.push(item.qElemNumber);
          }
        }
      } else {
        if (!this.childrenList[item.qText]) {
          return;
        }

        if (this.isSelected.includes(item.qState)) {
          currentSubSelectedValues = this.childrenList[item.qText].filter(x => this.isSelected.includes(x.qState)).map(x => x.qElemNumber);
        } else {
          currentSubSelectedValues = this.childrenList[item.qText].map(x => x.qElemNumber);
        }
      }

      this.qlikService.setFilter(qlikfield, currentSubSelectedValues, true, false);
    } else if (subId) {
      const selectedValue = typeof item.qNum === "string" ? item.qText : item.qNum.toString();
      let currentSubSelectedValues = [];

      if (this.childrenList[selectedValue] && this.childrenList[selectedValue].length > 0) {
        currentSubSelectedValues = this.childrenList[selectedValue].slice(0);

        currentSubSelectedValues = this.getSelectedList(currentSubSelectedValues);
        if (currentSubSelectedValues.length > 0) {
          this.qlikService.setFilter(subId, currentSubSelectedValues, true, false);
        }
      }

      this.qlikService.setFilter(id, typeof item.qNum === "string" ? item.qText : item.qNum, this.type() === "checkbox", this.isLocked());
    } else {
      if (!this.isDisabled.includes(item.qState)) {
        this.qlikService.setFilter(id, typeof item.qNum === "string" ? item.qText : item.qNum, this.type() === "checkbox", this.isLocked());
      }
    }
  }

  public reset() {
    this.qlikService
      .getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib?.clearSelections();
      })
      .unsubscribe();
  }
}
